import cx from 'classnames';
import type { FocusEvent } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';
import { useIsKeyboardNavigationState } from '../../hooks/useKeyboardNavigation.js';

export interface BoxSelectorProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onChange' | 'onFocus' | 'onBlur'
> {
  value: number | string | Array<any> | boolean;
  name: string;
  onChange: (value: unknown, e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  className?: string;
  disabled?: boolean;
  type?: 'radio' | 'checkbox';
  children: React.ReactNode;
  columnClassName?: string;
  mktSelector?: string;
  qaSelector?: string;
  isValid?: boolean;
}

interface BoxSelectorContextProps extends Pick<
  BoxSelectorProps,
  | 'name'
  | 'value'
  | 'type'
  | 'onFocus'
  | 'onBlur'
  | 'onChange'
  | 'disabled'
  | 'isValid'
> {}

const BoxSelectorContext = createContext<BoxSelectorContextProps | undefined>(
  undefined,
);

function BoxSelector({
  onChange,
  onBlur = () => {},
  onFocus = () => {},
  className,
  name,
  value,
  disabled,
  children,
  mktSelector,
  type = 'radio',
  isValid,
  columnClassName: _columnClassName,
  ...wrapperProps
}: BoxSelectorProps) {
  const providerData = useMemo(() => {
    return {
      name,
      value,
      type,
      onFocus,
      onBlur,
      onChange,
      disabled,
      isValid: isValid !== false,
    };
  }, [name, value, type, onFocus, onBlur, onChange, disabled, isValid]);
  return (
    <BoxSelectorContext.Provider value={providerData}>
      <div
        className={cx('box-selector', 'row', className)}
        data-mkt-selector={mktSelector}
        {...wrapperProps}
      >
        {children}
      </div>
    </BoxSelectorContext.Provider>
  );
}

export interface BoxColumnProps {
  className?: string;
  children?: React.ReactNode;
}

function BoxColumn({ className = 'col', children }: BoxColumnProps) {
  return (
    <div className={cx('box-selector__column', className)}>{children}</div>
  );
}

export interface BoxItemProps {
  children?:
    | React.ReactNode
    | ((params: { isSelected: boolean; selectedValue: string }) => JSX.Element);
  className?: string;
  selectedClassName?: string;
  disabledClassName?: string;
  value?: number | string | boolean;
  mktSelector?: string;
  qaSelector?: string;
  disabled?: boolean;
}

function BoxItem({
  children,
  value,
  className,
  selectedClassName = '',
  disabledClassName = '',
  mktSelector,
  qaSelector,
  disabled: itemDisabled = false,
}: BoxItemProps) {
  const {
    id,
    isSelected,
    disabled,
    selectedValue,
    name,
    type,
    onChange,
    classNames,
    onFocus: onFocusHandler,
    onBlur: onBlurHander,
  } = useBoxSelectorItem({
    value,
    className,
    selectedClassName,
    disabledClassName,
    disabled: itemDisabled,
  });

  const usableValue = typeof value === 'boolean' ? String(value) : value;
  return (
    <label
      htmlFor={id}
      className={classNames}
      data-qa-value={value}
      data-mkt-selector={mktSelector}
      data-qa-selector={qaSelector}
      aria-disabled={disabled}
    >
      {typeof children === 'function'
        ? //@ts-expect-error
          children({ isSelected, selectedValue })
        : children}
      <input
        className="visually-hidden"
        id={id}
        type={type}
        name={name}
        value={usableValue}
        //@ts-expect-error
        checked={isSelected}
        disabled={disabled}
        onChange={(e) => {
          if (!disabled) {
            onChange(value, e);
          }
        }}
        onBlur={onBlurHander}
        onFocus={onFocusHandler}
      />
    </label>
  );
}

interface UseBoxSelectorItem extends Pick<
  BoxItemProps,
  'value' | 'className' | 'selectedClassName' | 'disabledClassName' | 'disabled'
> {}

function useBoxSelectorItem(props: UseBoxSelectorItem) {
  const context = useContext(BoxSelectorContext);
  const isTabbing = useIsKeyboardNavigationState();
  const [hasFocus, setHasFocus] = useState(false);

  if (context === undefined) {
    throw new Error(
      'useBoxSelectorItem must be used within a BoxSelector element',
    );
  }

  const {
    name,
    value: selectedValue,
    type,
    onBlur,
    onFocus,
    disabled,
    isValid,
  } = context;
  const {
    value,
    className,
    selectedClassName = '',
    disabledClassName = '',
  } = props;

  const isDisabled = disabled || props.disabled;

  const hasFocusRing = !isDisabled && isTabbing && hasFocus;

  const id = `${name}_${value}`;

  const isSelected =
    type === 'checkbox'
      ? Array.isArray(selectedValue)
        ? selectedValue.includes(value)
        : selectedValue
      : value === selectedValue;

  const classNames = cx('box-selector__item', className, {
    'box-selector__item--is-selected': !isDisabled && isSelected,
    'box-selector__item--is-disabled': isDisabled,
    'box-selector__item--has-ring': hasFocusRing,
    'box-selector__item--has-error': !isValid,
    [selectedClassName]: isSelected,
    [disabledClassName]: isDisabled,
  });

  function onBlurHander(e: FocusEvent<HTMLInputElement>) {
    if (!isDisabled) {
      onBlur?.(e);
      setHasFocus(false);
    }
  }

  function onFocusHandler(e: FocusEvent<HTMLInputElement>) {
    if (!isDisabled) {
      onFocus?.(e);
      setHasFocus(true);
    }
  }

  return {
    ...context,
    id,
    isSelected,
    selectedValue,
    name,
    value: props.value,
    onBlur: onBlurHander,
    onFocus: onFocusHandler,
    hasFocus: hasFocus,
    hasFocusRing,
    classNames,
    isValid,
    disabled: isDisabled,
  };
}

export default BoxSelector;
export { BoxColumn, BoxItem, BoxSelector };
